import { z } from 'zod';
import { FIELD_KEY_PATTERN } from '../ids';

/** The 15 custom field types supported in v1. Later: date_range, files, formula, mirror, progress. */
export const FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'datetime',
  'boolean',
  'single_select',
  'multi_select',
  'user',
  'multi_user',
  'tags',
  'url',
  'email',
  'phone',
  'ticket_lookup',
] as const;
export const FieldTypeSchema = z.enum(FIELD_TYPES);
export type FieldType = z.infer<typeof FieldTypeSchema>;

export const TEXT_MAX_LENGTH = 255;
export const LONG_TEXT_MAX_LENGTH = 100_000;

/* ----------------------------- per-type config ----------------------------- */

const uuidList = z.array(z.uuid()).max(100);

export const FieldConfigSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('text'),
    maxLength: z.int().min(1).max(TEXT_MAX_LENGTH).default(TEXT_MAX_LENGTH),
    pattern: z.string().max(200).nullable().default(null),
  }),
  z.strictObject({
    type: z.literal('long_text'),
    maxLength: z.int().min(1).max(LONG_TEXT_MAX_LENGTH).default(LONG_TEXT_MAX_LENGTH),
  }),
  z.strictObject({
    type: z.literal('number'),
    min: z.number().nullable().default(null),
    max: z.number().nullable().default(null),
    /** Decimal places allowed, 0..6. */
    precision: z.int().min(0).max(6).default(2),
    unit: z.string().max(16).nullable().default(null),
  }),
  z.strictObject({ type: z.literal('date') }),
  z.strictObject({ type: z.literal('datetime') }),
  z.strictObject({ type: z.literal('boolean') }),
  z.strictObject({ type: z.literal('single_select') }),
  z.strictObject({
    type: z.literal('multi_select'),
    maxSelections: z.int().min(1).max(50).default(50),
  }),
  z.strictObject({
    type: z.literal('user'),
    restrictToRoleIds: uuidList.nullable().default(null),
  }),
  z.strictObject({
    type: z.literal('multi_user'),
    restrictToRoleIds: uuidList.nullable().default(null),
    maxSelections: z.int().min(1).max(50).default(50),
  }),
  z.strictObject({
    type: z.literal('tags'),
    maxTags: z.int().min(1).max(50).default(50),
  }),
  z.strictObject({ type: z.literal('url') }),
  z.strictObject({ type: z.literal('email') }),
  z.strictObject({ type: z.literal('phone') }),
  z.strictObject({
    type: z.literal('ticket_lookup'),
    allowedProjectIds: uuidList.nullable().default(null),
    allowedIssueTypeIds: uuidList.nullable().default(null),
  }),
]);
export type FieldConfig = z.infer<typeof FieldConfigSchema>;
export type FieldConfigInput = z.input<typeof FieldConfigSchema>;

/* ----------------------------- definitions ----------------------------- */

export const FIELD_SCOPES = ['project', 'tenant'] as const;
export const FieldScopeSchema = z.enum(FIELD_SCOPES);

export const FieldKeySchema = z.string().regex(FIELD_KEY_PATTERN, 'custom field key (cf_xxxxxxxx)');

export const FieldDefinitionSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  key: FieldKeySchema,
  /** Display label (column `label`); `name` mirrors it for older readers. */
  label: z.string().min(1).max(80),
  name: z.string().min(1).max(80),
  description: z.string().max(500).nullable(),
  type: FieldTypeSchema,
  config: FieldConfigSchema,
  scope: FieldScopeSchema,
  projectId: z.uuid().nullable(),
  /** Admin-flagged: value is projected into ticket_field_index for range filters and sorting. */
  indexed: z.boolean(),
  enforceUnique: z.boolean(),
  schemaVersion: z.int().min(1),
  archivedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;

export const FieldOptionSchema = z.object({
  id: z.uuid(),
  fieldId: z.uuid(),
  label: z.string().min(1).max(80),
  color: z.string().max(16).nullable(),
  position: z.int().min(0),
  archivedAt: z.iso.datetime().nullable(),
});
export type FieldOption = z.infer<typeof FieldOptionSchema>;

/** Per-project role restrictions on reading/editing a field. `null` means everyone with ticket access. */
export const FieldAccessSchema = z.object({
  readRoles: z.array(z.uuid()).nullable(),
  editRoles: z.array(z.uuid()).nullable(),
});
export type FieldAccess = z.infer<typeof FieldAccessSchema>;

/* ----------------------------- filter ops ----------------------------- */

export const FILTER_OPS = [
  'eq',
  'neq',
  'in',
  'not_in',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'is_null',
  'not_null',
  'contains',
  'starts_with',
  'any_of',
  'all_of',
  'none_of',
  'search',
] as const;
export const FilterOpSchema = z.enum(FILTER_OPS);
export type FilterOp = z.infer<typeof FilterOpSchema>;

const SCALAR_OPS = ['eq', 'neq', 'in', 'not_in', 'is_null', 'not_null'] as const;
const RANGE_OPS = ['gt', 'gte', 'lt', 'lte', 'between'] as const;
const TEXT_OPS = ['contains', 'starts_with'] as const;
const LIST_OPS = ['any_of', 'all_of', 'none_of', 'is_null', 'not_null'] as const;

export const FILTER_OPS_BY_TYPE: Record<FieldType, readonly FilterOp[]> = {
  text: [...SCALAR_OPS, ...TEXT_OPS],
  long_text: ['is_null', 'not_null', 'contains'],
  number: [...SCALAR_OPS, ...RANGE_OPS],
  date: [...SCALAR_OPS, ...RANGE_OPS],
  datetime: [...SCALAR_OPS, ...RANGE_OPS],
  boolean: ['eq', 'neq', 'is_null', 'not_null'],
  single_select: SCALAR_OPS,
  multi_select: LIST_OPS,
  user: SCALAR_OPS,
  multi_user: LIST_OPS,
  tags: LIST_OPS,
  url: [...SCALAR_OPS, ...TEXT_OPS],
  email: [...SCALAR_OPS, ...TEXT_OPS],
  phone: [...SCALAR_OPS, ...TEXT_OPS],
  ticket_lookup: SCALAR_OPS,
};

/** Types whose values can be projected into ticket_field_index (sort, range, unique). */
export const INDEXABLE_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'text',
  'number',
  'date',
  'datetime',
  'boolean',
  'single_select',
  'user',
  'url',
  'email',
  'phone',
  'ticket_lookup',
]);

export const isSortable = (type: FieldType): boolean => INDEXABLE_TYPES.has(type);
export const isMultiValued = (type: FieldType): boolean =>
  type === 'multi_select' || type === 'multi_user' || type === 'tags';

/* ----------------------------- value schemas ----------------------------- */

export interface FieldValueContext {
  /** Active option ids for select fields. Required for single_select/multi_select. */
  optionIds?: readonly string[];
}

const E164 = /^\+?[1-9]\d{6,14}$/;

/**
 * Builds the Zod schema for a single field VALUE from its definition. The caller wraps
 * it in `.optional()` unless the project binding marks the field required. Values are
 * stored exactly as validated here (option/user ids, ISO strings, numbers).
 */
export function buildFieldValueSchema(
  def: Pick<FieldDefinition, 'type' | 'config'>,
  ctx: FieldValueContext = {},
): z.ZodType {
  const cfg = def.config;
  switch (cfg.type) {
    case 'text': {
      let s = z.string().max(cfg.maxLength);
      if (cfg.pattern) {
        s = s.regex(new RegExp(cfg.pattern));
      }
      return s;
    }
    case 'long_text':
      return z.string().max(cfg.maxLength);
    case 'number': {
      let n = z.number().finite();
      if (cfg.min !== null) {
        n = n.min(cfg.min);
      }
      if (cfg.max !== null) {
        n = n.max(cfg.max);
      }
      const factor = 10 ** cfg.precision;
      return n.refine(
        (v) => Number.isInteger(Math.round(v * factor)) && v === Math.round(v * factor) / factor,
        {
          message: `at most ${cfg.precision} decimal places`,
        },
      );
    }
    case 'date':
      return z.iso.date();
    case 'datetime':
      // Normalized to UTC with milliseconds so lexical order equals chronological order.
      return z.iso.datetime({ offset: true }).transform((v) => new Date(v).toISOString());
    case 'boolean':
      return z.boolean();
    case 'single_select':
      return optionIdSchema(ctx);
    case 'multi_select':
      return z.array(optionIdSchema(ctx)).max(cfg.maxSelections);
    case 'user':
      return z.uuid();
    case 'multi_user':
      return z.array(z.uuid()).max(cfg.maxSelections);
    case 'tags':
      return z.array(z.string().min(1).max(40)).max(cfg.maxTags);
    case 'url':
      return z.url().max(2048);
    case 'email':
      return z.email().max(320);
    case 'phone':
      return z.string().regex(E164, 'E.164 phone number');
    case 'ticket_lookup':
      return z.uuid();
  }
}

function optionIdSchema(ctx: FieldValueContext): z.ZodType {
  const ids = ctx.optionIds;
  if (!ids || ids.length === 0) {
    return z.uuid();
  }
  const allowed = new Set(ids);
  return z.uuid().refine((v) => allowed.has(v), { message: 'unknown option' });
}

/* ----------------------------- Bolt 4: admin DTOs ----------------------------- */

/** Maximum number of indexed field bindings per project (`ticket_field_index` fan-out). */
export const INDEX_CAP_PER_PROJECT = 15;

/**
 * Validates and defaults a type-specific config. Issues keep their relative path so callers
 * can prefix them with `config`.
 */
export const parseFieldConfig = (type: FieldType, config: Record<string, unknown> = {}) =>
  FieldConfigSchema.safeParse({ ...config, type });

/**
 * `POST /fields` and `POST /projects/{id}/fields`. The key is generated by the API
 * (`newFieldKey()`); `config` is validated against the type and defaulted here, so the
 * output carries a complete `FieldConfig`.
 */
export const CreateFieldDefinitionSchema = z
  .strictObject({
    type: FieldTypeSchema,
    label: z.string().min(1).max(80),
    description: z.string().max(500).nullable().default(null),
    config: z.record(z.string(), z.unknown()).default({}),
    indexed: z.boolean().default(false),
    enforceUnique: z.boolean().default(false),
  })
  .transform((value, ctx) => {
    const parsed = parseFieldConfig(value.type, value.config);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        ctx.addIssue({ code: 'custom', message: issue.message, path: ['config', ...issue.path] });
      }
      return z.NEVER;
    }
    if (value.indexed && !INDEXABLE_TYPES.has(value.type)) {
      ctx.addIssue({
        code: 'custom',
        message: `${value.type} fields cannot be indexed`,
        path: ['indexed'],
      });
      return z.NEVER;
    }
    if (value.enforceUnique && !value.indexed) {
      ctx.addIssue({
        code: 'custom',
        message: 'enforceUnique requires indexed',
        path: ['enforceUnique'],
      });
      return z.NEVER;
    }
    return { ...value, config: parsed.data };
  });
export type CreateFieldDefinition = z.infer<typeof CreateFieldDefinitionSchema>;
export type CreateFieldDefinitionInput = z.input<typeof CreateFieldDefinitionSchema>;

/**
 * `PATCH /fields/{id}`: label, description, config, indexed, unique. The key is immutable
 * and the type changes only through `ChangeFieldTypeSchema`. `config` is validated by the
 * API against the stored type (`parseFieldConfig(existing.type, config)`).
 */
export const UpdateFieldDefinitionSchema = z
  .strictObject({
    label: z.string().min(1).max(80),
    description: z.string().max(500).nullable(),
    config: z.record(z.string(), z.unknown()),
    indexed: z.boolean(),
    enforceUnique: z.boolean(),
  })
  .partial();
export type UpdateFieldDefinition = z.infer<typeof UpdateFieldDefinitionSchema>;

export const CreateFieldOptionSchema = z.strictObject({
  label: z.string().min(1).max(80),
  color: z.string().max(16).nullable().default(null),
  /** Appended after the last option when omitted. */
  position: z.int().min(0).optional(),
});
export type CreateFieldOption = z.infer<typeof CreateFieldOptionSchema>;

export const UpdateFieldOptionSchema = z
  .strictObject({
    label: z.string().min(1).max(80),
    color: z.string().max(16).nullable(),
  })
  .partial();
export type UpdateFieldOption = z.infer<typeof UpdateFieldOptionSchema>;

/**
 * One `project_field` row (ADR-0013 §1). `defaultValue` is validated by the API against the
 * field's value schema; `issueTypeIds` null = all types.
 */
export const FieldBindingSchema = z.strictObject({
  fieldId: z.uuid(),
  required: z.boolean().default(false),
  defaultValue: z.unknown().optional(),
  position: z.int().min(0),
  access: FieldAccessSchema.nullable().default(null),
  issueTypeIds: z.array(z.uuid()).max(50).nullable().default(null),
});
export type FieldBinding = z.infer<typeof FieldBindingSchema>;

/** `PUT /projects/{id}/field-bindings`: the complete list, replaced atomically. */
export const PutFieldBindingsSchema = z
  .array(FieldBindingSchema)
  .max(200)
  .superRefine((bindings, ctx) => {
    const seen = new Set<string>();
    bindings.forEach((b, i) => {
      if (seen.has(b.fieldId)) {
        ctx.addIssue({
          code: 'custom',
          message: `field ${b.fieldId} is bound twice`,
          path: [i, 'fieldId'],
        });
      }
      seen.add(b.fieldId);
    });
  });
export type PutFieldBindings = z.infer<typeof PutFieldBindingsSchema>;

/** `POST /fields/{id}/change-type`; only whitelisted widenings are accepted. */
export const ChangeFieldTypeSchema = z.strictObject({
  toType: FieldTypeSchema,
});
export type ChangeFieldType = z.infer<typeof ChangeFieldTypeSchema>;

/**
 * Type-widening whitelist (ADR-0013 §2): in-place changes whose values need no
 * interpretation. Everything else is "archive and create a new field".
 */
export const TYPE_WIDENINGS: Record<FieldType, readonly FieldType[]> = {
  text: ['long_text'],
  long_text: [],
  number: ['text'],
  date: ['datetime'],
  datetime: [],
  boolean: [],
  single_select: ['multi_select'],
  multi_select: [],
  user: ['multi_user'],
  multi_user: [],
  tags: [],
  url: ['text'],
  email: ['text'],
  phone: ['text'],
  ticket_lookup: [],
};

export const canWiden = (from: FieldType, to: FieldType): boolean =>
  TYPE_WIDENINGS[from].includes(to);

/** `GET /projects/{id}/field-bindings` item: the binding joined with its field definition. */
export const ProjectFieldBindingSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  fieldId: z.uuid(),
  fieldKey: FieldKeySchema,
  fieldLabel: z.string(),
  fieldType: FieldTypeSchema,
  fieldScope: FieldScopeSchema,
  required: z.boolean(),
  defaultValue: z.unknown().optional(),
  position: z.int().min(0),
  access: FieldAccessSchema.nullable(),
  issueTypeIds: z.array(z.uuid()).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ProjectFieldBinding = z.infer<typeof ProjectFieldBindingSchema>;
